import { useCallback, useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { DatePicker } from 'components/date-picker'
import { Header } from 'components/layout/header'
import { Main } from 'components/layout/main'
import { Search } from 'components/search'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import {
  FilterState,
  PERIODS,
  initialFilters,
  periodLabel,
  productChips,
  toParams,
} from 'helpers/twe-filters'
import { ImageOff, Loader, SlidersHorizontal, X } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store/store'
import { fetchMostClicked, fetchTweFilters } from 'store/twe/tweSlice'
import type { MostClickedProduct } from 'types/twe'
import { TweDailyClicksChart } from 'ui/components/TweDailyClicksChart'
import { TweFiltersSheet } from 'ui/components/TweFiltersSheet'

const LIMITS = ['10', '25', '50', '100']

const eur = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
})

const price = (product: MostClickedProduct) =>
  product.min_price === product.max_price
    ? eur.format(product.min_price)
    : `${eur.format(product.min_price)} – ${eur.format(product.max_price)}`

const Stat = ({ value }: { value: number }) => (
  <b className='font-semibold text-foreground tabular-nums'>
    {value.toLocaleString('it-IT')}
  </b>
)

const ProductRow = ({
  product,
  rank,
  share,
  barWidth,
}: {
  product: MostClickedProduct
  rank: number
  share: number
  barWidth: number
}) => (
  <li className='grid grid-cols-[2rem_2.75rem_minmax(0,1fr)] items-center gap-x-3 gap-y-2 border-t py-3 first:border-t-0 sm:grid-cols-[2.5rem_3rem_minmax(0,1fr)_11rem] sm:gap-x-4'>
    <span
      className={`text-right text-2xl leading-none font-bold tabular-nums ${
        rank <= 3 ? 'text-foreground' : 'text-muted-foreground/50'
      }`}
    >
      {rank}
    </span>

    {product.photo ? (
      <img
        src={product.photo}
        alt=''
        className='h-11 w-11 rounded-md border object-contain'
      />
    ) : (
      <div className='flex h-11 w-11 items-center justify-center rounded-md border text-muted-foreground'>
        <ImageOff className='h-4 w-4' />
      </div>
    )}

    <div className='min-w-0'>
      <Link
        to='/twe/prodotti/$id'
        params={{ id: String(product.id) }}
        className='block truncate text-sm font-bold hover:underline'
        title={product.title}
      >
        {product.title}
      </Link>
      <div className='mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground'>
        {product.categories.map((category) => (
          <Badge key={category.id} variant='outline' className='text-[10px]'>
            {category.name}
          </Badge>
        ))}
        <span className='tabular-nums'>{price(product)}</span>
        {product.former_price != null && (
          <span className='line-through'>
            {eur.format(product.former_price)}
          </span>
        )}
        {product.on_sale && <Badge className='text-[10px]'>Saldo</Badge>}
        {product.discountable && (
          <Badge variant='outline' className='text-[10px] text-violet-600'>
            Coupon
          </Badge>
        )}
      </div>
    </div>

    <div className='col-start-3 sm:col-start-4 sm:text-right'>
      <span className='text-lg font-semibold tabular-nums'>
        {product.clicks_count.toLocaleString('it-IT')}
      </span>{' '}
      <span className='text-xs text-muted-foreground tabular-nums'>
        {share.toLocaleString('it-IT', { maximumFractionDigits: 1 })}%
      </span>
      <div className='mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted'>
        <div
          className='h-full rounded-full bg-lime-400 dark:bg-lime-300'
          style={{ width: `${barWidth}%` }}
        />
      </div>
    </div>
  </li>
)

const TweMostClickedPage = () => {
  const dispatch = useAppDispatch()
  const { loading, error, products, summary, daily, categories, brands } =
    useAppSelector((state) => state.twe)
  const [filters, setFilters] = useState<FilterState>(initialFilters)
  const [sheetOpen, setSheetOpen] = useState(false)

  // Period and Top N are the only two levers the endpoint has, so they apply on click
  const apply = useCallback(
    (next: FilterState) => {
      setFilters(next)
      dispatch(fetchMostClicked(toParams(next)))
    },
    [dispatch]
  )

  useEffect(() => {
    dispatch(fetchTweFilters())
    dispatch(fetchMostClicked(toParams(initialFilters)))
  }, [dispatch])

  const chips = productChips(filters, categories)
  const totalClicks =
    summary?.clicks_count ??
    products.reduce((sum, p) => sum + p.clicks_count, 0)
  const maxClicks = products[0]?.clicks_count ?? 0

  return (
    <>
      {/* ===== Top Heading ===== */}
      <Header>
        <div className='ml-auto flex items-center space-x-4'>
          <Search />
        </div>
      </Header>

      {/* ===== Main ===== */}
      <Main>
        <div className='mb-2 flex flex-wrap items-center justify-between space-y-2 gap-x-4'>
          <div>
            <h2 className='text-2xl font-bold tracking-tight'>
              Prodotti TWE più cliccati
            </h2>
            <p className='text-muted-foreground'>
              I filtri sui prodotti valgono prima della classifica: filtrando
              per brand ottieni la top N di quel brand.
            </p>
          </div>
        </div>

        <div className='-mx-4 mt-5 flex-1 overflow-auto px-4 py-1'>
          <div className='flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 p-3'>
            <div className='flex flex-wrap gap-1'>
              {PERIODS.map((period) => (
                <Button
                  key={period.key}
                  size='sm'
                  variant={
                    filters.period === period.key ? 'secondary' : 'ghost'
                  }
                  onClick={() => apply({ ...filters, period: period.key })}
                >
                  {period.label}
                </Button>
              ))}
              <Button
                size='sm'
                variant={filters.period === 'custom' ? 'secondary' : 'ghost'}
                onClick={() => setFilters({ ...filters, period: 'custom' })}
              >
                Su misura
              </Button>
            </div>

            {/* The pickers are 240px each: they'd wrap the bar if always shown */}
            {filters.period === 'custom' && (
              <>
                <DatePicker
                  selected={filters.dateFrom}
                  onSelect={(date) =>
                    apply({ ...filters, period: 'custom', dateFrom: date })
                  }
                  placeholder='Dal'
                />
                <DatePicker
                  selected={filters.dateTo}
                  onSelect={(date) =>
                    apply({ ...filters, period: 'custom', dateTo: date })
                  }
                  placeholder='Al'
                />
              </>
            )}

            <span className='ms-2 text-xs font-medium tracking-wide text-muted-foreground uppercase'>
              Top
            </span>
            <div className='flex overflow-hidden rounded-md border'>
              {LIMITS.map((limit) => (
                <button
                  key={limit}
                  type='button'
                  aria-pressed={filters.limit === limit}
                  onClick={() => apply({ ...filters, limit })}
                  className={`h-8 px-3 text-sm tabular-nums not-first:border-s ${
                    filters.limit === limit
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {limit}
                </button>
              ))}
            </div>

            <Button
              variant='outline'
              className='ms-auto'
              onClick={() => setSheetOpen(true)}
            >
              <SlidersHorizontal className='h-4 w-4' />
              Filtri prodotto
              {chips.length > 0 && (
                <Badge className='ms-1 px-1.5'>{chips.length}</Badge>
              )}
            </Button>
          </div>

          <div className='mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground'>
            {chips.map((chip) => (
              <button
                key={chip.key}
                type='button'
                onClick={() =>
                  apply({ ...filters, [chip.key]: initialFilters[chip.key] })
                }
                className='inline-flex items-center gap-1.5 rounded-full border bg-background py-1 ps-3 pe-2 text-xs hover:bg-accent'
              >
                {chip.label}
                <X className='h-3 w-3' />
              </button>
            ))}
            {!loading && products.length > 0 && (
              <span className='ms-auto'>
                <Stat value={totalClicks} /> click ·{' '}
                {summary ? (
                  <>
                    <Stat value={summary.products_count} /> prodotti su{' '}
                    <Stat value={summary.catalogue_products_count} /> ·{' '}
                    <span title='Utenti loggati che hanno cliccato (i click anonimi restano nel totale)'>
                      <Stat value={summary.users_count} /> utenti
                    </span>{' '}
                  </>
                ) : (
                  <>
                    <Stat value={products.length} /> prodotti{' '}
                  </>
                )}
                · {periodLabel(filters)}
              </span>
            )}
          </div>

          {loading ? (
            <Loader className='mt-6 h-5 w-5 animate-spin' />
          ) : error ? (
            <p className='mt-6 text-red-500'>Errore: {error}</p>
          ) : products.length === 0 ? (
            <p className='mt-6 text-muted-foreground'>
              Nessun click nel periodo selezionato.
            </p>
          ) : (
            <>
              <div className='mt-4 rounded-lg border p-4'>
                <h3 className='text-sm font-semibold'>Click per giorno</h3>
                <p className='text-xs text-muted-foreground'>
                  Tutti i click del periodo sui prodotti filtrati, non solo
                  sulla top {filters.limit}
                </p>
                <div className='mt-3'>
                  <TweDailyClicksChart daily={daily} />
                </div>
              </div>
              <ol className='mt-4'>
                {products.map((product, index) => (
                  <ProductRow
                    key={product.id}
                    product={product}
                    rank={index + 1}
                    share={
                      totalClicks
                        ? (product.clicks_count / totalClicks) * 100
                        : 0
                    }
                    barWidth={
                      maxClicks ? (product.clicks_count / maxClicks) * 100 : 0
                    }
                  />
                ))}
              </ol>
            </>
          )}
        </div>
      </Main>

      <TweFiltersSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        filters={filters}
        categories={categories}
        brands={brands}
        onApply={apply}
      />
    </>
  )
}

export default TweMostClickedPage
